import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Box,
  Button,
  Typography,
  TextField,
  InputBase,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Divider,
  Skeleton,
  Tooltip,
} from "@mui/material";
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
  RestartAlt as RestartAltIcon,
} from "@mui/icons-material";
import { useSelector, useDispatch } from "react-redux";
import { SCREEN_IDS } from "../components/FullComponent.js";
import { typeConnect, backgroundColor2 } from "../constants/Constants";
import {
  getFullMo,
  insertNewWeight,
  insertControlDePieza,
} from "../services/ConnectDataBase";
import { getCurrentDateTime, formatDate } from "../services/Functions";
import { updateListaPesos } from "../redux/reducers";
import { translateText } from "../translations/translationService";
import DeleteWeightButton from "../components/Pesajes/ComponentDeleteWeightButton";
import { WEIGHING_ADMIN_SECTION_COLORS } from "../style/colors.js";

export default function WeighingAdminSection() {
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(true);
  const scanInputRef = useRef(null);
  const screenName = "WeighingAdminSection";
  const currentScreenId = SCREEN_IDS.ADMINWEIGHT;
  const tipo_tiempo = "WEIGHT";
  const lang = useSelector((state) => state.lang.value);
  const listData = useSelector((state) => state.pesosList.listaPesos);
  const [successLines, setSuccessLines] = useState([]);
  const [refSearch, setRefSearch] = useState("");
  const usuario = useSelector((state) => state.user.name);
  const equipo = useSelector((state) => state.user.team);
  const [searchedMOs, setSearchedMOs] = useState([]);
  const [searchResults, setSearchResults] = useState([]);
  const [weights, setWeights] = useState({});
  const [isResetModalOpen, setIsResetModalOpen] = useState(false);

  const getMo = useCallback(async () => {
    setIsLoading(true);
    try {
      const fullMo = await getFullMo(
        screenName,
        typeConnect[0],
        currentScreenId
      );

      const moArray = Object.entries(fullMo).map(([po, mo]) => ({
        key: po,
        po,
        ...mo,
      }));
      setIsLoading(false);
      dispatch(updateListaPesos(moArray));

      const initialWeights = moArray.reduce((acc, item) => {
        acc[item.key] = "";
        return acc;
      }, {});
      setWeights(initialWeights);
    } catch (error) {
      setIsLoading(false);
      console.error(error);
    }
  }, [dispatch]);

  useEffect(() => {
    getMo();
  }, [getMo]);

  const performSearch = () => {
    handleSearch(refSearch);
  };

  const handleWeightDeleted = () => {
    getMo();
  };

  const handleWeightChange = (mo, weight) => {
    if (/^\d+(\.\d*)?$/.test(weight) || weight === "") {
      setWeights((prevWeights) => ({
        ...prevWeights,
        [mo]: weight,
      }));
    }
  };
  const toggleResetModal = () => {
    setIsResetModalOpen((prev) => !prev);
  };

  const handleSearch = (searchValue) => {
    const moArray = searchValue
      .replace(/'/g, "-")
      .split(" ")
      .map((mo) => mo.trim());

    const uniqueResults = new Set();

    moArray.forEach((mo) => {
      const result = listData.find((item) => item.mo === mo);
      if (result) {
        uniqueResults.add(result);
      } else {
        console.log(`La MO ${mo} no está en la lista.`);
      }
    });

    const uniqueResultsArray = Array.from(uniqueResults);
    setSearchedMOs(uniqueResultsArray.map((result) => result.mo));
    setSearchResults(uniqueResultsArray);

    setRefSearch("");
  };

  const handleAdd = async (item) => {
    const weight = weights[item.key];
    if (weight && parseFloat(weight) !== 0) {
      const status = await insertNewWeight(
        item.po,
        item.mo,
        usuario,
        equipo,
        1,
        weight,
        getCurrentDateTime(),
        "Clasif.",
        tipo_tiempo,
        currentScreenId,
        item.cantidad
      );
      if (status) {
        await handleAddRecibido(item);
        const updatedListData = listData.filter(
          (rowData) => rowData.mo !== item.mo
        );
        setSuccessLines((prevSuccessLines) => [...prevSuccessLines, item.key]);
        dispatch(updateListaPesos(updatedListData));
        setWeights((prevWeights) => {
          const newWeights = { ...prevWeights };
          delete newWeights[item.key];
          return newWeights;
        });
        console.log("Peso actualizado.");
      }
    } else {
      console.log("Valor de peso inválido.");
    }
  };

  const handleAddRecibido = async (item) => {
    try {
      await insertControlDePieza(
        item.po,
        item.mo,
        usuario,
        1,
        getCurrentDateTime(),
        "Entregado.",
        SCREEN_IDS.PARTS_MOVEMENTS
      );
      console.log("Entrega registrada correctamente.");
    } catch (error) {
      console.error("Error al registrar la entrega:", error);
    }
  };

  return (
    <Box sx={{ padding: 1.5 }}>
      <DeleteWeightButton
        currentScreenId={currentScreenId}
        onWeightDeleted={handleWeightDeleted}
        isResetModalOpen={isResetModalOpen}
        toggleResetModal={toggleResetModal}
      />
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          gap: 1.5,
          padding: 1.5,
          backgroundColor: "white",
          borderRadius: 2,
          boxShadow:
            "0 1px 3px rgba(0, 0, 0, 0.1), 0 1px 2px rgba(0, 0, 0, 0.06)",
          border: `1px solid ${WEIGHING_ADMIN_SECTION_COLORS.BORDER_LIGHT || "#e5e7eb"}`,
          marginBottom: 1.5,
        }}>
        <Box sx={{ flex: 1, minWidth: "280px", position: "relative" }}>
          <InputBase
            inputRef={scanInputRef}
            value={refSearch || ""}
            onChange={(e) => {
              let inputValue = e.target.value.replace(/'/g, "-");

              if (/^\d{6}-\d{3}$/.test(inputValue.split(" ").slice(-1)[0])) {
                inputValue += " ";
              }

              setRefSearch(inputValue);
            }}
            placeholder={translateText("Código de MO", lang)}
            sx={{
              width: "100%",
              backgroundColor: WEIGHING_ADMIN_SECTION_COLORS.BACKGROUND_CARD || "#F9FAFB",
              color: WEIGHING_ADMIN_SECTION_COLORS.TEXT_CARD || "#374151",
              fontSize: "0.85rem",
              fontWeight: 500,
              padding: "8px 12px 8px 40px",
              borderRadius: 2,
              border: `1.5px solid ${WEIGHING_ADMIN_SECTION_COLORS.BORDER_CARD || "#e5e7eb"}`,
              transition: "all 0.2s ease",
              "&:hover": {
                backgroundColor: WEIGHING_ADMIN_SECTION_COLORS.BACKGROUND_INPUT || "#F3F4F6",
                borderColor: WEIGHING_ADMIN_SECTION_COLORS.BORDER_BRAND || "#174045",
                boxShadow: "0 2px 4px rgba(70, 93, 100, 0.1)",
              },
              "&.Mui-focused": {
                backgroundColor: "white",
                borderColor: WEIGHING_ADMIN_SECTION_COLORS.BORDER_BRAND || "#174045",
                boxShadow:
                  "0 0 0 2px rgba(70, 93, 100, 0.1), 0 2px 4px rgba(70, 93, 100, 0.1)",
              },
              "& input": {
                padding: 0,
              },
              "& input::placeholder": {
                color: WEIGHING_ADMIN_SECTION_COLORS.TEXT_MUTED || "#9CA3AF",
                opacity: 1,
              },
            }}
          />

          <Box
            sx={{
              position: "absolute",
              left: 16,
              top: "50%",
              transform: "translateY(-50%)",
              color: WEIGHING_ADMIN_SECTION_COLORS.TEXT_MUTED || "#9CA3AF",
              pointerEvents: "none",
            }}>
            <SearchIcon sx={{ fontSize: 18 }} />
          </Box>
        </Box>

        <Button
          onClick={performSearch}
          variant='contained'
          size="small"
          startIcon={<SearchIcon />}
          sx={{
            backgroundColor: WEIGHING_ADMIN_SECTION_COLORS.BACKGROUND_BUTTON_PRIMARY || "#174045",
            color: "white",
            borderRadius: 2,
            fontSize: "0.8rem",
            fontWeight: 600,
            padding: "6px 16px",
            textTransform: "none",
            boxShadow: "none",
            transition: "all 0.2s ease",
            "&:hover": {
              backgroundColor: WEIGHING_ADMIN_SECTION_COLORS.BACKGROUND_BUTTON_SECONDARY || "#1e4d52",
              transform: "translateY(-1px)",
              boxShadow: "0 4px 12px rgba(70, 93, 100, 0.2)",
            },
          }}>
          {translateText("Buscar", lang)}
        </Button>

        <Box
          sx={{
            display: { xs: "none", lg: "block" },
            width: "1px",
            height: 32,
            backgroundColor: WEIGHING_ADMIN_SECTION_COLORS.BORDER_CARD || "#e5e7eb",
          }}
        />

        <Typography variant='h6' sx={{ flex: 1, textAlign: "center", fontSize: "1rem", fontWeight: 600 }}>
          {translateText("Tabla de MO", lang)}
        </Typography>

        <IconButton
          onClick={toggleResetModal}
          sx={{
            padding: 1,
            color: WEIGHING_ADMIN_SECTION_COLORS.TEXT_BRAND || "#174045",
            transition: "all 0.2s ease",
            "&:hover": {
              backgroundColor: "rgba(70, 93, 100, 0.08)",
              transform: "scale(1.1)",
            },
          }}>
          <RestartAltIcon />
        </IconButton>
      </Box>
      {isLoading ? (
        <Paper
          sx={{
            borderRadius: 2,
            overflow: "hidden",
            boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
            border: `1px solid ${WEIGHING_ADMIN_SECTION_COLORS.BORDER_LIGHT || "#e5e7eb"}`,
          }}>
          <Box sx={{ p: 2 }}>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    {[
                      translateText("PO", lang),
                      translateText("MO", lang),
                      translateText("Cantidad", lang),
                      translateText("Referencia", lang),
                      translateText("Entrega", lang),
                      translateText("Stamping", lang),
                      translateText("Observaciones", lang),
                      translateText("Peso gramos metal", lang),
                      translateText("Registrar Peso", lang),
                    ].map((header, i) => (
                      <TableCell key={i} sx={{ py: 1, px: 1.5 }}>
                        <Skeleton variant="text" width="100%" height={24} />
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {[...Array(5)].map((_, i) => (
                    <TableRow key={i}>
                      {[...Array(9)].map((_, j) => (
                        <TableCell key={j} sx={{ py: 0.75, px: 1.5 }}>
                          <Skeleton variant="text" width="100%" height={20} />
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </Paper>
      ) : listData.length === 0 ? (
        <Paper
          sx={{
            borderRadius: 2,
            overflow: "hidden",
            boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
            border: `1px solid ${WEIGHING_ADMIN_SECTION_COLORS.BORDER_LIGHT || "#e5e7eb"}`,
            textAlign: "center",
            py: 8,
          }}>
          <Typography variant='h6' sx={{ color: WEIGHING_ADMIN_SECTION_COLORS.TEXT_SECONDARY || "#6B7280" }}>
            {translateText("Todos los pesos añadidos", lang)}
          </Typography>
        </Paper>
      ) : (
        <Paper
          sx={{
            borderRadius: 2,
            overflow: "hidden",
            boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
            border: `1px solid ${WEIGHING_ADMIN_SECTION_COLORS.BORDER_LIGHT || "#e5e7eb"}`,
          }}>
          <TableContainer sx={{ maxHeight: "calc(78vh - 60px)" }}>
            <Table stickyHeader size="small" sx={{ width: "100%" }}>
              <TableHead>
                <TableRow>
                  <TableCell
                    align='center'
                    sx={{
                      fontWeight: 600,
                      backgroundColor: WEIGHING_ADMIN_SECTION_COLORS.BACKGROUND_TABLE_HEADER || "#F9FAFB",
                      borderBottom: `1px solid ${WEIGHING_ADMIN_SECTION_COLORS.BORDER_TABLE || "#e5e7eb"}`,
                      fontSize: "0.75rem",
                      color: "black",
                      py: 1,
                      px: 1.5,
                    }}>
                    {translateText("PO", lang)}
                  </TableCell>
                  <TableCell
                    align='center'
                    sx={{
                      fontWeight: 600,
                      backgroundColor: WEIGHING_ADMIN_SECTION_COLORS.BACKGROUND_TABLE_HEADER || "#F9FAFB",
                      borderBottom: `1px solid ${WEIGHING_ADMIN_SECTION_COLORS.BORDER_TABLE || "#e5e7eb"}`,
                      fontSize: "0.75rem",
                      color: "black",
                      py: 1,
                      px: 1.5,
                    }}>
                    {translateText("MO", lang)}
                  </TableCell>
                  <TableCell
                    align='center'
                    sx={{
                      fontWeight: 600,
                      backgroundColor: WEIGHING_ADMIN_SECTION_COLORS.BACKGROUND_TABLE_HEADER || "#F9FAFB",
                      borderBottom: `1px solid ${WEIGHING_ADMIN_SECTION_COLORS.BORDER_TABLE || "#e5e7eb"}`,
                      fontSize: "0.75rem",
                      color: "black",
                      py: 1,
                      px: 1.5,
                    }}>
                    {translateText("Cantidad", lang)}
                  </TableCell>
                  <TableCell
                    align='center'
                    sx={{
                      fontWeight: 600,
                      backgroundColor: WEIGHING_ADMIN_SECTION_COLORS.BACKGROUND_TABLE_HEADER || "#F9FAFB",
                      borderBottom: `1px solid ${WEIGHING_ADMIN_SECTION_COLORS.BORDER_TABLE || "#e5e7eb"}`,
                      fontSize: "0.75rem",
                      color: "black",
                      py: 1,
                      px: 1.5,
                    }}>
                    {translateText("Referencia", lang)}
                  </TableCell>
                  <TableCell
                    align='center'
                    sx={{
                      fontWeight: 600,
                      backgroundColor: WEIGHING_ADMIN_SECTION_COLORS.BACKGROUND_TABLE_HEADER || "#F9FAFB",
                      borderBottom: `1px solid ${WEIGHING_ADMIN_SECTION_COLORS.BORDER_TABLE || "#e5e7eb"}`,
                      fontSize: "0.75rem",
                      color: "black",
                      py: 1,
                      px: 1.5,
                    }}>
                    {translateText("Entrega", lang)}
                  </TableCell>
                  <TableCell
                    align='center'
                    sx={{
                      fontWeight: 600,
                      backgroundColor: WEIGHING_ADMIN_SECTION_COLORS.BACKGROUND_TABLE_HEADER || "#F9FAFB",
                      borderBottom: `1px solid ${WEIGHING_ADMIN_SECTION_COLORS.BORDER_TABLE || "#e5e7eb"}`,
                      fontSize: "0.75rem",
                      color: "black",
                      py: 1,
                      px: 1.5,
                    }}>
                    {translateText("Stamping", lang)}
                  </TableCell>
                  <TableCell
                    align='center'
                    sx={{
                      fontWeight: 600,
                      backgroundColor: WEIGHING_ADMIN_SECTION_COLORS.BACKGROUND_TABLE_HEADER || "#F9FAFB",
                      borderBottom: `1px solid ${WEIGHING_ADMIN_SECTION_COLORS.BORDER_TABLE || "#e5e7eb"}`,
                      fontSize: "0.75rem",
                      color: "black",
                      py: 1,
                      px: 1.5,
                    }}>
                    {translateText("Observaciones", lang)}
                  </TableCell>
                  <TableCell
                    align='center'
                    sx={{
                      fontWeight: 600,
                      backgroundColor: WEIGHING_ADMIN_SECTION_COLORS.BACKGROUND_TABLE_HEADER || "#F9FAFB",
                      borderBottom: `1px solid ${WEIGHING_ADMIN_SECTION_COLORS.BORDER_TABLE || "#e5e7eb"}`,
                      fontSize: "0.75rem",
                      color: "black",
                      py: 1,
                      px: 1.5,
                    }}>
                    {translateText("Peso gramos metal", lang)}
                  </TableCell>
                  <TableCell
                    align='center'
                    sx={{
                      fontWeight: 600,
                      backgroundColor: WEIGHING_ADMIN_SECTION_COLORS.BACKGROUND_TABLE_HEADER || "#F9FAFB",
                      borderBottom: `1px solid ${WEIGHING_ADMIN_SECTION_COLORS.BORDER_TABLE || "#e5e7eb"}`,
                      fontSize: "0.75rem",
                      color: "black",
                      py: 1,
                      px: 1.5,
                    }}>
                    {translateText("Registrar Peso", lang)}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(searchResults.length > 0 ? searchResults : listData).map(
                  (item) => {
                    const moWeight = weights[item.key] || "";
                    const isSuccessLine = successLines.includes(item.key);
                    if (isSuccessLine) return null;
                    return (
                      <TableRow
                        key={item.key}
                        sx={{
                          "&:hover": {
                            backgroundColor: WEIGHING_ADMIN_SECTION_COLORS.BACKGROUND_ROW_ALTERNATE || "#F9FAFB",
                          },
                          borderBottom: `1px solid ${WEIGHING_ADMIN_SECTION_COLORS.BORDER_LIGHT || "#e5e7eb"}`,
                        }}>
                        <TableCell
                          align='center'
                          sx={{
                            fontSize: "0.8rem",
                            py: 0.75,
                            px: 1.5,
                            color: WEIGHING_ADMIN_SECTION_COLORS.TEXT_PRIMARY || "#374151",
                          }}>
                          {item.po}
                        </TableCell>
                        <TableCell
                          align='center'
                          sx={{
                            fontSize: "0.8rem",
                            py: 0.75,
                            px: 1.5,
                            color: WEIGHING_ADMIN_SECTION_COLORS.TEXT_PRIMARY || "#374151",
                          }}>
                          {item.mo}
                        </TableCell>
                        <TableCell
                          align='center'
                          sx={{
                            fontSize: "0.8rem",
                            py: 0.75,
                            px: 1.5,
                            color: WEIGHING_ADMIN_SECTION_COLORS.TEXT_PRIMARY || "#374151",
                          }}>
                          {item.cantidad}
                        </TableCell>
                        <TableCell
                          align='center'
                          sx={{
                            fontSize: "0.8rem",
                            py: 0.75,
                            px: 1.5,
                            color: WEIGHING_ADMIN_SECTION_COLORS.TEXT_PRIMARY || "#374151",
                          }}>
                          {item.referencia}
                        </TableCell>
                        <TableCell
                          align='center'
                          sx={{
                            fontSize: "0.8rem",
                            py: 0.75,
                            px: 1.5,
                            color: WEIGHING_ADMIN_SECTION_COLORS.TEXT_PRIMARY || "#374151",
                          }}>
                          {formatDate(item.fechaentrega)}
                        </TableCell>
                        <TableCell
                          align='center'
                          sx={{
                            fontSize: "0.8rem",
                            py: 0.75,
                            px: 1.5,
                            color: WEIGHING_ADMIN_SECTION_COLORS.TEXT_PRIMARY || "#374151",
                          }}>
                          {item.stamping}
                        </TableCell>
                        <TableCell
                          align='center'
                          sx={{
                            fontSize: "0.8rem",
                            py: 0.75,
                            px: 1.5,
                            color: WEIGHING_ADMIN_SECTION_COLORS.TEXT_PRIMARY || "#374151",
                            maxWidth: 200,
                          }}>
                          <Tooltip title={item.observaciones || ""} arrow>
                            <Typography
                              sx={{
                                fontSize: "0.75rem",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                                cursor: "help",
                              }}>
                              {item.observaciones || ""}
                            </Typography>
                          </Tooltip>
                        </TableCell>
                        <TableCell align='center' sx={{ py: 0.75, px: 1.5 }}>
                          <TextField
                            value={moWeight}
                            onChange={(e) =>
                              handleWeightChange(item.key, e.target.value)
                            }
                            type='number'
                            size='small'
                            sx={{
                              "& .MuiOutlinedInput-root": {
                                borderRadius: 1,
                                fontSize: "0.75rem",
                                backgroundColor: "white",
                                height: "28px",
                                width: "100px",
                                "& fieldset": {
                                  borderColor: WEIGHING_ADMIN_SECTION_COLORS.BORDER_LIGHT || "#e5e7eb",
                                },
                                "&:hover fieldset": {
                                  borderColor: WEIGHING_ADMIN_SECTION_COLORS.BORDER_BRAND || "#174045",
                                },
                                "&.Mui-focused fieldset": {
                                  borderColor: WEIGHING_ADMIN_SECTION_COLORS.BORDER_BRAND || "#174045",
                                  borderWidth: "1px",
                                },
                              },
                              "& .MuiOutlinedInput-input": {
                                textAlign: "center",
                                fontSize: "0.75rem",
                                py: 0.5,
                              },
                            }}
                          />
                        </TableCell>
                        <TableCell align='center' sx={{ py: 0.75, px: 1.5 }}>
                          <Button
                            onClick={() => handleAdd(item)}
                            variant='contained'
                            sx={{
                              width: "120px",
                              padding: "4px 12px",
                              fontSize: "0.75rem",
                              height: "28px",
                              backgroundColor: backgroundColor2,
                              textTransform: "none",
                              borderRadius: 1,
                              boxShadow: "none",
                              transition: "all 0.2s ease",
                              "&:hover": {
                                backgroundColor: WEIGHING_ADMIN_SECTION_COLORS.BACKGROUND_HOVER || "#1e4d52",
                                transform: "translateY(-1px)",
                                boxShadow: "0 2px 4px rgba(70, 93, 100, 0.2)",
                              },
                            }}>
                            {translateText("Registrar", lang)}
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  }
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}
    </Box>
  );
}
